// fixed-assets-web/src/packages/auth/view/AccountRecovery.jsx
// @mui
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Container,
    IconButton,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
// components
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Logo from '../../../components/logo';
// sections
import { Page } from '../../../components';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import { userLogout } from '../../../shared/auth/authService';
import { resetPassword, setNewPassword, verifyResetOTP } from '../store/authActions';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

export default function AccountRecovery() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, isSuccess, isVerified, hasSentOTP } = useSelector((state) => state.authReducer);

  // local UI state
  const [otp, setOTP] = useState('');
  const [email, setEmail] = useState('');

  // Schemas
  const accountRecoverySchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Enter a valid email address')
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Please enter a valid email format')
      .lowercase('Email must be in lowercase'),
  });

  const otpSchema = Yup.object().shape({
    otp: Yup.string()
      .required('OTP is required')
      .matches(/^\d{6}$/, 'OTP must be exactly 6 digits')
      .length(6, 'OTP must be 6 digits'),
  });

  // Match password policy used elsewhere: min 8, upper, lower, digit, special
  const newPasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required('New password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).+$/,
        'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'
      ),
    confirmNewPassword: Yup.string()
      .required('Please confirm your new password')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
  });

  // default values
  const defaultValues = { email: '' };
  const otpDefaultValues = { otp: '' };
  const passwordDefaultValues = { newPassword: '', confirmNewPassword: '' };

  // forms
  const methods = useForm({ resolver: yupResolver(accountRecoverySchema), defaultValues });
  const otpMethods = useForm({ resolver: yupResolver(otpSchema), defaultValues: otpDefaultValues });
  const passwordMethods = useForm({
    resolver: yupResolver(newPasswordSchema),
    defaultValues: passwordDefaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // pull watch from passwordMethods for live feedback
  const {
    watch: watchNewPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { isSubmitting: isPasswordSubmitting },
    reset: resetPasswordForm,
  } = passwordMethods;
  const watchedNewPassword = watchNewPassword('newPassword') || '';

  const passwordChecks = {
    length: watchedNewPassword.length >= 8,
    lower: /[a-z]/.test(watchedNewPassword),
    upper: /[A-Z]/.test(watchedNewPassword),
    digit: /\d/.test(watchedNewPassword),
    special: /[^A-Za-z0-9]/.test(watchedNewPassword),
  };

  const passedCount = Object.values(passwordChecks).filter(Boolean).length;
  const strength = Math.round((passedCount / 5) * 100);

  const strengthLabel =
    passedCount <= 1
      ? 'Very Weak'
      : passedCount === 2
      ? 'Weak'
      : passedCount === 3
      ? 'Fair'
      : passedCount === 4
      ? 'Good'
      : 'Strong';

  // top-level form helpers
  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const {
    reset: resetOtp,
    handleSubmit: handleOtpSubmit,
    formState: { isSubmitting: isOtpSubmitting },
  } = otpMethods;

  // actions
  const verifyOTP = async (data) => {
    if (email && data.otp) {
      const payload = { email, otp: parseInt(data.otp, 10) };
      try {
        dispatch(verifyResetOTP(payload));
        resetOtp();
        setOTP('');
      } catch (error) {
        console.error(error);
        setOTP('');
      }
    }
  };

  const handleNewPassword = async (data) => {
    if (email && data.newPassword) {
      const payload = { email, new_password: data.newPassword };
      try {
        dispatch(setNewPassword(payload));
        resetPasswordForm();
        setEmail('');
      } catch (error) {
        console.error(error);
        setEmail('');
      }
    }
  };

  const onSubmit = async (data) => {
    setEmail(data.email);
    try {
      const payload = { email: data.email };
      dispatch(resetPassword(payload));
      reset();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      resetOtp();
      resetPasswordForm();
      setEmail('');
      setOTP('');
      userLogout();
    }
  }, [isSuccess, reset, resetOtp, resetPasswordForm, navigate]);

  // forms UI
  const PasswordResetForm = () => (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ my: 3 }}>
        <RHFTextField name="email" label="Email" autoComplete="off" />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          <Link component={RouterLink} to="/login" variant="subtitle2" underline="hover">
            Login Instead?
          </Link>
        </Stack>
        <LoadingButton
          loading={isLoading}
          disabled={isSubmitting}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
        >
          Reset Password
        </LoadingButton>
      </Stack>
    </FormProvider>
  );

  const OtpForm = () => (
    <FormProvider methods={otpMethods} onSubmit={handleOtpSubmit(verifyOTP)}>
      <Stack spacing={3} sx={{ my: 3 }}>
        <RHFTextField
          name="otp"
          label="Enter OTP"
          autoFocus
          placeholder="Enter 6-digit OTP"
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 6,
            onKeyPress: (event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            },
          }}
          helperText="Enter the 6-digit code sent to your email"
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          <Link component={RouterLink} to="/login" variant="subtitle2" underline="hover">
            Login Instead?
          </Link>
        </Stack>
        <LoadingButton
          loading={isLoading}
          disabled={isOtpSubmitting}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
        >
          Verify OTP
        </LoadingButton>
      </Stack>
    </FormProvider>
  );

  const NewPasswordForm = () => {
    // local UI: single global show/hide toggle for the new password form
    const [showPasswords, setShowPasswords] = useState(false);

    return (
      <FormProvider methods={passwordMethods} onSubmit={handlePasswordSubmit(handleNewPassword)}>
        <Stack spacing={3} sx={{ my: 3 }}>
          {/* global toggle for show/hide */}
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              onClick={() => setShowPasswords((s) => !s)}
              aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}
              size="small"
            >
              {showPasswords ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </Box>

          <RHFTextField
            name="newPassword"
            label="New Password"
            autoFocus
            placeholder="Enter new password"
            autoComplete="new-password"
            type={showPasswords ? 'text' : 'password'}
            helperText="Password must be at least 8 characters with uppercase, lowercase, number and special character"
          />

          {/* Inline help + live strength meter */}
          <Box sx={{ width: '100%', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Password requirements:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              At least 8 characters, including uppercase, lowercase, number and special character
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={strength} sx={{ height: 8, borderRadius: 1 }} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  {strengthLabel}
                </Typography>
              </Box>
            </Box>

            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.length ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="At least 8 characters" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.upper ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One uppercase letter (A-Z)" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.lower ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One lowercase letter (a-z)" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.digit ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One number (0-9)" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.special ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One special character (e.g. !@#$%)" />
              </ListItem>
            </List>
          </Box>

          <RHFTextField
            name="confirmNewPassword"
            type={showPasswords ? 'text' : 'password'}
            label="Confirm New Password"
          />

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
            <Link component={RouterLink} to="/login" variant="subtitle2" underline="hover">
              Login Instead?
            </Link>
          </Stack>
          <LoadingButton
            loading={isLoading}
            disabled={isPasswordSubmitting}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
          >
            Set New Password
          </LoadingButton>
        </Stack>
      </FormProvider>
    );
  };

  return (
    <>
      <Page title="Account Recovery">
        <StyledRoot>
          <Logo
            sx={{
              position: 'fixed',
              top: { xs: 16, sm: 24, md: 40 },
              left: { xs: 16, sm: 24, md: 40 },
            }}
          />
          <Container maxWidth="sm">
            <StyledContent>
              <Typography variant="h5" alignItems="center" gutterBottom>
                Account Recovery
              </Typography>

              {(hasSentOTP && <OtpForm />) || (isVerified && <NewPasswordForm />) || <PasswordResetForm />}
            </StyledContent>
          </Container>
        </StyledRoot>
      </Page>
    </>
  );
}
